import { FocusEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'

import useScrolledElement from './useScrolledElement'
import { isElementOverflownX } from '../utils/helper'

export type ScrollableTabListItem = {
	id: string
}

type UseScrollableTabListParams<T extends ScrollableTabListItem> = {
	selectedId: string | undefined
	items: T[]
	getTabButtonId: (id: string) => string
	onSelect: (item: T) => void
}

const scrollTabIntoView = (container: HTMLElement, tabButton: HTMLElement) => {
	const containerRect = container.getBoundingClientRect()
	const tabRect = tabButton.getBoundingClientRect()

	const tabLeft = tabRect.left - containerRect.left
	const tabRight = tabRect.right - containerRect.left
	const containerWidth = containerRect.width

	if (tabLeft < 0 || tabRight > containerWidth) {
		const newScrollLeft = tabButton.offsetLeft - container.clientWidth / 2 + tabButton.clientWidth / 2
		const maxScrollLeft = container.scrollWidth - container.clientWidth
		const boundedScrollLeft = Math.min(Math.max(newScrollLeft, 0), maxScrollLeft)

		container.scrollTo({
			left: boundedScrollLeft,
			behavior: 'smooth'
		})
	}
}

const getNextTabIndex = (currentIndex: number, key: string, itemsLength: number): number | null => {
	switch (key) {
		case 'ArrowLeft':
			return currentIndex === 0 ? itemsLength - 1 : currentIndex - 1
		case 'ArrowRight':
			return currentIndex === itemsLength - 1 ? 0 : currentIndex + 1
		case 'Home':
			return 0
		case 'End':
			return itemsLength - 1
		default:
			return null
	}
}

const useScrollableTabList = <T extends ScrollableTabListItem, TElement extends HTMLElement = HTMLElement>(params: UseScrollableTabListParams<T>) => {
	const { selectedId, items, getTabButtonId, onSelect } = params

	const scrollElementRef = useRef<TElement | null>(null)
	const getTabButtonIdRef = useRef(getTabButtonId)
	const onSelectRef = useRef(onSelect)
	const itemsRef = useRef(items)

	getTabButtonIdRef.current = getTabButtonId
	onSelectRef.current = onSelect
	itemsRef.current = items

	const { isScrolledLeft, isScrolledRight, scrollLeft, scrollRight } = useScrolledElement(scrollElementRef.current)
	const [isOverflown, setIsOverflown] = useState(false)

	useEffect(() => {
		const container = scrollElementRef.current
		if (!container) {
			return undefined
		}

		const checkOverflow = () => {
			setIsOverflown(isElementOverflownX(container))
		}

		checkOverflow()

		const resizeObserver = new ResizeObserver(checkOverflow)
		resizeObserver.observe(container)

		if (container.firstElementChild) {
			resizeObserver.observe(container.firstElementChild)
		}

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	useEffect(() => {
		if (scrollElementRef.current && selectedId) {
			const tabButton = document.getElementById(getTabButtonIdRef.current(selectedId))

			if (tabButton) {
				scrollTabIntoView(scrollElementRef.current, tabButton)
			}
		}
	}, [selectedId])

	const focusAndSelect = (item: T) => {
		onSelectRef.current(item)

		requestAnimationFrame(() => {
			const tabButton = document.getElementById(getTabButtonIdRef.current(item.id))
			tabButton?.focus()

			if (scrollElementRef.current && tabButton) {
				scrollTabIntoView(scrollElementRef.current, tabButton)
			}
		})
	}

	const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentItem: T) => {
		const currentItems = itemsRef.current
		const currentIndex = currentItems.findIndex((item) => item.id === currentItem.id)

		if (currentIndex === -1) {
			return
		}

		const nextIndex = getNextTabIndex(currentIndex, event.key, currentItems.length)

		if (nextIndex === null) {
			return
		}

		event.preventDefault()
		const nextItem = currentItems[nextIndex]

		if (nextItem) {
			focusAndSelect(nextItem)
		}
	}

	const handleTabFocus = (event: FocusEvent<HTMLButtonElement>) => {
		if (scrollElementRef.current) {
			scrollTabIntoView(scrollElementRef.current, event.currentTarget)
		}
	}

	return {
		scrollElementRef,
		isOverflown,
		isScrolledLeft,
		isScrolledRight,
		scrollLeft,
		scrollRight,
		handleTabKeyDown,
		handleTabFocus
	} as const
}

export default useScrollableTabList
